<template>
    <re-b-modal ref="modal"
                :header="editing ? $t('label.sith.tpm.furnaceHeatOrder.editTitle') : $t('label.sith.tpm.furnaceHeatOrder.createTitle')"
                :ok-label="$t('label.sith.tpm.furnaceHeatOrder.save')"
                :ok-loading="saving"
                no-auto-hide
                disable-scrollable
                enable-popover
                no-close-on-backdrop
                @ok="submit"
                @hide="onHide">
        <form class="furnace-heat-order-form" @submit.prevent="submit">
            <div class="form-row">
                <div class="form-group col-sm-6">
                    <label for="fhoOrderCode" class="mb-1">
                        {{ $t('label.sith.tpm.furnaceHeatOrder.orderCode') }}<span class="text-danger ml-1">*</span>
                    </label>
                    <input v-if="editing" id="fhoOrderCode" class="form-control form-control-sm" :value="orderCode" readonly>
                    <single-suggest-input v-else ref="order" v-model="orderCode"
                                          input-id="fhoOrderCode"
                                          :fetch="fetchOrders"
                                          :placeholder="$t('label.sith.tpm.opcPointCurve.orderCodePlaceholder')"
                                          :empty-text="$t('label.sith.tpm.opcPointCurve.noOrderMatch')"
                                          @select="onOrderSelect">
                        <template #item="{item}">
                            <span>{{ item.value }}</span>
                            <span v-if="item.raw.projectCode" class="small text-muted ml-2">{{ item.raw.projectCode }}</span>
                        </template>
                    </single-suggest-input>
                </div>
                <div class="form-group col-sm-6">
                    <label for="fhoOperationSeq" class="mb-1">
                        {{ $t('label.sith.tpm.furnaceHeatOrder.operationSeq') }}<span class="text-danger ml-1">*</span>
                        <i v-if="loadingOperations" class="fa fa-fw fa-spinner fa-spin text-muted"></i>
                    </label>
                    <!-- :key 随工单号变：SingleSuggestInput 内部 fetched 缓存不跟工单走，换工单（或工单号还没选时聚焦过）后必须重建，聚焦才会重新拉该工单的工序 -->
                    <input v-if="editing" id="fhoOperationSeq" class="form-control form-control-sm" :value="operationSeq" readonly>
                    <single-suggest-input v-else ref="operation" v-model="operationSeq"
                                          :key="'fhoOperation#' + trimmedOrderCode"
                                          input-id="fhoOperationSeq"
                                          :min-length="0"
                                          :fetch="fetchOperationItems"
                                          :placeholder="$t('label.sith.tpm.opcPointCurve.operationSeqPlaceholder')"
                                          :empty-text="operationsEmptyText"></single-suggest-input>
                </div>
            </div>
            <div class="form-row">
                <div class="form-group col-sm-6">
                    <label for="fhoProjectCode" class="mb-1">
                        {{ $t('label.sith.tpm.furnaceHeatOrder.projectCode') }}
                        <i v-if="loadingInfo" class="fa fa-fw fa-spinner fa-spin text-muted"></i>
                    </label>
                    <input id="fhoProjectCode" class="form-control form-control-sm" :value="projectCode" readonly>
                </div>
                <div class="form-group col-sm-6">
                    <label for="fhoMaterialCode" class="mb-1">{{ $t('label.sith.tpm.furnaceHeatOrder.materialCode') }}</label>
                    <input id="fhoMaterialCode" class="form-control form-control-sm" :value="materialCode" readonly>
                </div>
            </div>
            <div class="form-group">
                <label for="fhoDeviceCode" class="mb-1">
                    {{ $t('label.sith.tpm.furnaceHeatOrder.deviceCode') }}<span class="text-danger ml-1">*</span>
                </label>
                <input id="fhoDeviceCode" type="text" class="form-control form-control-sm" autocomplete="off"
                       :placeholder="$t('label.sith.tpm.furnaceHeatOrder.deviceCodePlaceholder')"
                       v-model="deviceCode" @blur="checkDevice">
                <small v-if="deviceHint.text" class="form-text" :class="deviceHint.warn ? 'text-warning' : 'text-muted'">
                    <i v-if="deviceHint.warn" class="fa fa-fw fa-exclamation-triangle"></i>{{ deviceHint.text }}
                </small>
            </div>
            <div class="form-row">
                <div class="form-group col-sm-6">
                    <label for="fhoStart" class="mb-1">
                        {{ $t('label.sith.tpm.furnaceHeatOrder.startTime') }}<span class="text-danger ml-1">*</span>
                    </label>
                    <input id="fhoStart" type="datetime-local" class="form-control form-control-sm" v-model="start">
                </div>
                <div class="form-group col-sm-6">
                    <label for="fhoEnd" class="mb-1">
                        {{ $t('label.sith.tpm.furnaceHeatOrder.endTime') }}<span class="text-danger ml-1">*</span>
                    </label>
                    <input id="fhoEnd" type="datetime-local" class="form-control form-control-sm" v-model="end">
                </div>
            </div>
            <div class="small text-muted border-top pt-2">
                <i class="fa fa-fw fa-hashtag"></i>{{ $t('label.sith.tpm.furnaceHeatOrder.queryKey') }}：
                <span v-if="editing" class="text-monospace text-dark">{{ queryKey }}</span>
                <span v-else>{{ $t('label.sith.tpm.furnaceHeatOrder.queryKeyPreview') }}</span>
            </div>
        </form>
    </re-b-modal>
</template>

<script>
import ReBModal from "@/components/echarts-part/CalendarShift/cells/stuffs/ReBModal.vue";
import SingleSuggestInput from "@/components/common/inputs/SingleSuggestInput.vue";
import {
    createRecord,
    fetchOperations,
    fetchOrderInfo,
    fetchOrderSuggest,
    updateRecord,
    userMessageOf
} from "@/components/SithMes/tpm/opcCurve/opcCurveQueryApi";
import {parseInputTime, toApiTime, toInputTime} from "@/components/SithMes/tpm/opcCurve/opcCurveSettings";

const ORDER_DEBOUNCE_MS = 300;

/**
 * 炉子加热工单 新增 / 修改弹窗（spec 2026-10-26 验收 #2–#5）。
 * 新增：工单号联想 → 带出项目号 / 物料编码（选中候选直接取，手输则防抖调 orderInfo）、列出该工单工序；
 *       记录编号由后端生成（同一工单工序重复保存加 -1、-2…）。
 * 修改：工单号 / 工序号 / 项目号 / 物料编码只读，只能改设备编码与时间段。
 * 设备编码失焦时查 opc-server 的点位：显示设备名称，没有点位只警告不拦截（后端只校验非空）。
 *
 * 用法：this.$refs.modal.openCreate() / this.$refs.modal.openEdit(row)；保存成功 emit saved(vo)
 */
export default {
    name: "FurnaceHeatOrderModal",
    components: {ReBModal, SingleSuggestInput},
    data() {
        return {
            editingId: null,
            queryKey: '',
            orderCode: '',
            operationSeq: '',
            projectCode: '',
            materialCode: '',
            deviceCode: '',
            start: '',
            end: '',
            // 已带出项目号 / 物料编码的工单号（null = 未带出）
            infoOf: null,
            loadingInfo: false,
            operationsOf: null,
            operations: [],
            loadingOperations: false,
            // {text, warn}
            deviceHint: {text: '', warn: false},
            saving: false
        };
    },
    computed: {
        editing() {
            return this.editingId !== null;
        },
        trimmedOrderCode() {
            return (this.orderCode || '').trim();
        },
        operationsEmptyText() {
            if (!this.trimmedOrderCode) {
                return this.$t('label.sith.tpm.opcPointCurve.orderFirst');
            }
            return this.operationsOf === this.trimmedOrderCode && !this.operations.length
                ? this.$t('label.sith.tpm.opcPointCurve.noOperations')
                : this.$t('label.sith.tpm.opcPointCurve.noOperationMatch');
        }
    },
    watch: {
        // 新增模式下工单号变了：清掉旧的带出值，防抖后按新工单号带出项目号 / 物料编码
        trimmedOrderCode(code) {
            if (this.editing) {
                return;
            }
            this.clearOrderTimer();
            if (code === this.infoOf) {
                return;
            }
            this.orderSeq++;
            this.infoOf = null;
            this.projectCode = '';
            this.materialCode = '';
            this.operationSeq = '';
            this.operations = [];
            this.operationsOf = null;
            this.loadingInfo = false;
            if (code) {
                this.orderTimer = setTimeout(() => this.loadOrderInfo(code), ORDER_DEBOUNCE_MS);
            }
        }
    },
    created() {
        // 非响应式：工单带出的防抖定时器与请求代次、设备校验代次
        this.orderTimer = null;
        this.orderSeq = 0;
        this.deviceSeq = 0;
    },
    beforeDestroy() {
        this.clearOrderTimer();
    },
    methods: {
        openCreate() {
            this.reset();
            this.$refs.modal.showModal();
            this.$nextTick(() => this.$refs.order && this.$refs.order.focus());
        },
        openEdit(row) {
            this.reset();
            this.editingId = row.id;
            this.queryKey = row.queryKey || '';
            this.orderCode = row.orderCode || '';
            this.operationSeq = row.operationSeq || '';
            this.projectCode = row.projectCode || '';
            this.materialCode = row.materialCode || '';
            this.deviceCode = row.deviceCode || '';
            const start = parseInputTime(row.startTime);
            const end = parseInputTime(row.endTime);
            this.start = start ? toInputTime(start) : '';
            this.end = end ? toInputTime(end) : '';
            this.$refs.modal.showModal();
            this.checkDevice();
        },
        reset() {
            this.clearOrderTimer();
            this.orderSeq++;
            this.deviceSeq++;
            this.editingId = null;
            this.queryKey = '';
            this.orderCode = '';
            this.operationSeq = '';
            this.projectCode = '';
            this.materialCode = '';
            this.deviceCode = '';
            this.start = '';
            this.end = '';
            this.infoOf = null;
            this.loadingInfo = false;
            this.operationsOf = null;
            this.operations = [];
            this.loadingOperations = false;
            this.deviceHint = {text: '', warn: false};
            this.saving = false;
        },
        onHide() {
            this.clearOrderTimer();
            this.orderSeq++;
            this.deviceSeq++;
        },
        clearOrderTimer() {
            if (this.orderTimer) {
                clearTimeout(this.orderTimer);
                this.orderTimer = null;
            }
        },
        toast(message, variant) {
            this.$bvToast.toast(message, {
                title: this.$t('title.sithMes.tpm.furnaceHeatOrder'),
                variant: variant || 'warning',
                solid: true,
                autoHideDelay: 4000
            });
        },
        fetchOrders(keyword) {
            return fetchOrderSuggest(this.$store, keyword).then(list => list.map(o => ({
                value: o.orderCode,
                label: o.projectCode ? o.orderCode + ' · ' + o.projectCode : o.orderCode,
                raw: o
            })));
        },
        // 选中候选：直接用候选里的项目号 / 物料编码，不再等防抖请求
        onOrderSelect(item) {
            this.clearOrderTimer();
            this.orderSeq++;
            this.infoOf = item.value;
            this.projectCode = (item.raw && item.raw.projectCode) || '';
            this.materialCode = (item.raw && item.raw.materialCode) || '';
            this.loadingInfo = false;
            this.operationSeq = '';
            this.$nextTick(() => this.$refs.operation && this.$refs.operation.focus());
        },
        loadOrderInfo(code) {
            this.clearOrderTimer();
            const seq = ++this.orderSeq;
            this.loadingInfo = true;
            return fetchOrderInfo(this.$store, code).then(info => {
                if (seq !== this.orderSeq) {
                    return;
                }
                this.infoOf = code;
                this.projectCode = (info && info.projectCode) || '';
                this.materialCode = (info && info.materialCode) || '';
            }).catch(() => {
                // 工单不存在：保持空白，保存时后端给出「工单不存在」
            }).finally(() => {
                if (seq === this.orderSeq) {
                    this.loadingInfo = false;
                }
            });
        },
        loadOperations(code) {
            this.loadingOperations = true;
            return fetchOperations(this.$store, code).then(list => {
                if (this.trimmedOrderCode === code) {
                    this.operations = list;
                    this.operationsOf = code;
                }
            }).catch(() => {
                this.operations = [];
                this.operationsOf = null;
            }).finally(() => {
                this.loadingOperations = false;
            });
        },
        // 工序号候选：该工单的工序按输入过滤（本地列表，加载一次）
        fetchOperationItems(keyword) {
            const code = this.trimmedOrderCode;
            if (!code) {
                return Promise.resolve([]);
            }
            const ready = this.operationsOf === code ? Promise.resolve() : this.loadOperations(code);
            return ready.then(() => {
                const kw = (keyword || '').trim().toLowerCase();
                return this.operations
                    .filter(op => !kw || op.toLowerCase().indexOf(kw) !== -1)
                    .map(op => ({value: op, label: op, raw: op}));
            });
        },
        checkDevice() {
            const code = (this.deviceCode || '').trim();
            const seq = ++this.deviceSeq;
            if (!code) {
                this.deviceHint = {text: '', warn: false};
                return;
            }
            this.deviceHint = {text: this.$t('label.sith.tpm.furnaceHeatOrder.deviceChecking'), warn: false};
            this.$store.dispatch('api/get', {
                url: process.env.VUE_APP_EM_OPCCLIENT_URL + '/emOpcPointCurve/points?deviceCode=' + encodeURIComponent(code)
            }).then(vo => {
                if (seq !== this.deviceSeq) {
                    return;
                }
                const points = (vo && vo.points) || [];
                if (!points.length) {
                    this.deviceHint = {text: this.$t('label.sith.tpm.furnaceHeatOrder.deviceNoPoints', {code}), warn: true};
                    return;
                }
                // 回显订阅表里的原始写法，曲线页按它查
                this.deviceCode = vo.deviceCode || code;
                this.deviceHint = {text: [vo.deviceCode, vo.deviceName].filter(Boolean).join(' '), warn: false};
            }).catch(() => {
                if (seq === this.deviceSeq) {
                    this.deviceHint = {text: '', warn: false};
                }
            });
        },
        submit() {
            if (this.saving) {
                return;
            }
            const deviceCode = (this.deviceCode || '').trim();
            const start = parseInputTime(this.start);
            const end = parseInputTime(this.end);
            if (!this.editing && !this.trimmedOrderCode) {
                this.toast(this.$t('label.sith.tpm.furnaceHeatOrder.orderRequired'));
                return;
            }
            if (!this.editing && !(this.operationSeq || '').trim()) {
                this.toast(this.$t('label.sith.tpm.furnaceHeatOrder.operationRequired'));
                return;
            }
            if (!deviceCode) {
                this.toast(this.$t('label.sith.tpm.furnaceHeatOrder.deviceRequired'));
                return;
            }
            if (!start || !end) {
                this.toast(this.$t('label.sith.tpm.furnaceHeatOrder.timeRequired'));
                return;
            }
            if (start.getTime() >= end.getTime()) {
                this.toast(this.$t('label.sith.tpm.furnaceHeatOrder.startBeforeEnd'));
                return;
            }
            const times = {startTime: toApiTime(start), endTime: toApiTime(end)};
            const request = this.editing
                ? updateRecord(this.$store, Object.assign({id: this.editingId, deviceCode}, times))
                : createRecord(this.$store, Object.assign({
                    orderCode: this.trimmedOrderCode,
                    operationSeq: this.operationSeq.trim(),
                    deviceCode
                }, times));
            this.saving = true;
            request.then(vo => {
                this.saving = false;
                const key = (vo && vo.queryKey) || this.queryKey;
                this.toast(this.$t(this.editing ? 'label.sith.tpm.furnaceHeatOrder.updated' : 'label.sith.tpm.furnaceHeatOrder.saved', {key}), 'success');
                this.$emit('saved', vo);
                this.$refs.modal.hide();
            }).catch(error => {
                this.saving = false;
                const message = userMessageOf(error, 400) || userMessageOf(error, 409);
                if (message) {
                    this.toast(message);
                } else if (error && error.response) {
                    this.$store.dispatch('api/error', {error});
                } else {
                    console.error(error);
                    this.toast(this.$t('label.sith.tpm.furnaceHeatOrder.saveFailed'), 'danger');
                }
            });
        }
    }
};
</script>

<style scoped>
.furnace-heat-order-form label {
    font-weight: normal;
}
</style>
